import React, { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLanguages } from "../../context/LanguagesContext";
import { useLoader } from "../../context/LoaderContext";
import { usePortal } from "../../context/PortalsContext";
import { useTranslation } from "../../context/TranslationContext";
import { useAppBootstrap } from "../../hooks/useAppBootstrap";
import { useGuestBootstrap } from "../../hooks/useGuestBootstrap";
import { getAllPortalTranslations } from "../../services/api/apiClient";
import { GlobalLoader } from "../GlobalLoader/GlobalLoader";

export const AppBootstrap: React.FC = () => {
  const location = useLocation();
  const { auth } = useAuth();
  const { portals } = usePortal();
  const { languages } = useLanguages();
  const { dispatch: translationsDispatch } = useTranslation();
  const { loader, dispatch: loaderDispatch } = useLoader();
  const { isAppReady, isInitialized } = loader;
  const runBootstrap = useAppBootstrap();
  const runGuestBootstrap = useGuestBootstrap();

  const isErrorRoute = location.pathname.startsWith("/error/");

  // Refs to detect transitions across re-renders.
  const hasBootstrappedRef = useRef(false);
  const previousPortalUidRef = useRef<string | null>(null);
  const previousLanguageRef = useRef<string | null>(null);

  // Effect 1: Initial bootstrap (mount)
  // Login transition is handled inside Login.tsx, which calls /bootstrap directly
  // and dispatches the data — so we deliberately do NOT re-fire on guest → auth.
  useEffect(() => {
    if (isErrorRoute) return;
    if (auth.loadStatus !== "loaded") return;

    const handleAuthBootstrap = async () => {
      loaderDispatch({ type: "SET_INITIALIZED", payload: false });
      try {
        await runBootstrap();
      } catch (err: any) {
        console.error("Auth bootstrap failed:", err);
        const status = err?.response?.status;

        // Token expired. Fall back to a guest bootstrap so translations are populated
        // for the login screen we're about to redirect to.
        if (status === 401 || status === 403) {
          localStorage.removeItem("userName");
          localStorage.removeItem("rememberMe");
          try {
            await runGuestBootstrap();
          } catch (guestErr) {
            console.error("Guest bootstrap fallback also failed:", guestErr);
          }
        }
      } finally {
        loaderDispatch({ type: "SET_INITIALIZED", payload: true });
        loaderDispatch({ type: "SET_APP_READY", payload: true });
      }
    };

    const handleGuestBootstrap = async () => {
      loaderDispatch({ type: "SET_INITIALIZED", payload: false });
      try {
        runGuestBootstrap();
      } catch (err) {
        console.error("Guest bootstrap failed:", err);
      } finally {
        loaderDispatch({ type: "SET_INITIALIZED", payload: true });
        loaderDispatch({ type: "SET_APP_READY", payload: true });
      }
    };

    // First mount: pick the right bootstrap based on auth state.
    if (!hasBootstrappedRef.current) {
      hasBootstrappedRef.current = true;
      const shouldRestore =
        !!localStorage.getItem("userName") &&
        !!localStorage.getItem("rememberMe");
      if (shouldRestore) {
        handleAuthBootstrap();
      } else {
        handleGuestBootstrap();
      }
      return;
    }
    // Login transition (null → user) is intentionally NOT handled here —
    // Login.tsx dispatches the bootstrap data itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.loadStatus, auth.user, isErrorRoute, loaderDispatch]);

  // Effect 2: Mid-session portal switch → refetch translations + clear cached page customisations.
  // Does NOT re-fetch user data (per the design — roles persist across portal lens).
  useEffect(() => {
    if (isErrorRoute) return;
    if (!isAppReady) return;

    const currentPortalUid = portals.selectedPortal?.portalUid ?? null;
    if (!currentPortalUid) return;

    if (!previousPortalUidRef.current) {
      previousPortalUidRef.current = currentPortalUid;
      return;
    }
    if (previousPortalUidRef.current === currentPortalUid) return;
    previousPortalUidRef.current = currentPortalUid;

    const refetchForPortal = async () => {
      loaderDispatch({ type: "SET_INITIALIZED", payload: false });
      try {
        // Note: RESET_ALL_PAGES is dispatched at the portal-switch source
        // (useSelectPortal hook) so it happens in the same batch as
        // SET_SELECTED_PORTAL — preventing children from rendering with the
        // new portalUid against the previous portal's stale customisations.
        const translations = await getAllPortalTranslations(
          currentPortalUid,
          languages.selectedLanguage,
        );
        translationsDispatch({ type: "SET_ALL", payload: translations });
      } catch (err) {
        console.error("Portal-change refetch failed:", err);
      } finally {
        loaderDispatch({ type: "SET_INITIALIZED", payload: true });
      }
    };

    refetchForPortal();
  }, [
    portals.selectedPortal,
    languages.selectedLanguage,
    isAppReady,
    isErrorRoute,
    translationsDispatch,
    loaderDispatch,
  ]);

  // Effect 3: Mid-session language switch → refetch translations only.
  // Customisations untouched (not language-scoped).
  useEffect(() => {
    if (isErrorRoute) return;
    if (!isAppReady) return;

    const currentLang = languages.selectedLanguage;
    const portalUid = portals.selectedPortal?.portalUid;
    if (!currentLang || !portalUid) return;

    if (!previousLanguageRef.current) {
      previousLanguageRef.current = currentLang;
      return;
    }
    if (previousLanguageRef.current === currentLang) return;
    previousLanguageRef.current = currentLang;

    const refetchForLanguage = async () => {
      loaderDispatch({ type: "SET_INITIALIZED", payload: false });
      try {
        const translations = await getAllPortalTranslations(
          portalUid,
          currentLang,
        );
        translationsDispatch({ type: "SET_ALL", payload: translations });
      } catch (err) {
        console.error("Language-change refetch failed:", err);
      } finally {
        loaderDispatch({ type: "SET_INITIALIZED", payload: true });
      }
    };

    refetchForLanguage();
  }, [
    languages.selectedLanguage,
    portals.selectedPortal,
    isAppReady,
    isErrorRoute,
    translationsDispatch,
    loaderDispatch,
  ]);

  if (isErrorRoute) return <Outlet />;
  if (!isAppReady) return <GlobalLoader />;
  return (
    <>
      {!isInitialized && <GlobalLoader />}
      <Outlet />
    </>
  );
};
